import React, { Fragment, useState, useEffect } from 'react';
import { Field, FormSection } from 'redux-form';
import { withTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { subjectListByEmployeeQuery } from '../../../../shared/queries/Subject';
import { employeeListByFilterWithAccessQuery } from '../../../../shared/queries/Employee';
import DateField from '../../../../shared/components/form/DateField';
import Select from '../../../../shared/components/form/Select';
import GraphQL from '../../../../GraphQL';
import { COMMUNICATION_TYPE } from '../../../../shared/utils/constants';
import { showNotificationAutoDestroy } from '../../../../shared/components/ShowNotification';
import SelectVirtualized from '../../../../shared/components/form/SelectVirtualized';
import { relationObjectListByParamsQuery } from '../../../../shared/queries/RelationObject';
import renderField from '../../../../shared/components/form/RenderField';

const TaskFields = (props) => {
  const { t, showLoading, hideLoading, change, editable } = props;
  const [subjects, setSubjects] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [recipients, setRecipients] = useState([]);

  const loadRecipients = () => {
    showLoading();
    const query = relationObjectListByParamsQuery(true, false, true, true);
    GraphQL(query, {})
      .then((res) => {
        res.relationObjectListByParams = res.relationObjectListByParams.map(
          (item) => ({
            ...item,
            value: item.identifier,
            label: item.description,
          })
        );
        setRecipients(res.relationObjectListByParams);
        hideLoading();
      })
      .catch(() => {
        hideLoading();
        showNotificationAutoDestroy(
          'danger',
          t('errors.title'),
          t('errors.error-to-loading')
        );
      });
  };

  function loadSubjects(employeeId) {
    showLoading();
    const query = subjectListByEmployeeQuery(employeeId);
    GraphQL(query, {})
      .then((res) => {
        res.subjectListByEmployee = res.subjectListByEmployee.map((subj) => ({
          ...subj,
          value: subj.id,
          label: subj.name,
        }));
        setSubjects(res.subjectListByEmployee);
        if (res.subjectListByEmployee.length === 1) {
          change('taskHeader.subject', {
            ...res.subjectListByEmployee[0],
            value: res.subjectListByEmployee[0].id,
            label: res.subjectListByEmployee[0].name,
          });
        }
        hideLoading();
      })
      .catch(() => {
        hideLoading();
        showNotificationAutoDestroy(
          'danger',
          t('errors.title'),
          t('errors.error-to-loading')
        );
      });
  }

  const onRecipientChange = (values) => {
    if (values) {
      change('recipients', values);
    } else {
      change('recipients', []);
    }
  };

  function loadEmployees() {
    const filter = {
      id: 0,
      usuarioChatInterno: false,
      ativo: true,
      filterByStatus: true,
      pedagogicAdm: false,
      updaterId: 0,
      usuarioChat: false,
      active: true,
    };
    const query = employeeListByFilterWithAccessQuery(filter);
    GraphQL(query, {})
      .then((res) => {
        res.employeeListByFilterWithAccess =
          res.employeeListByFilterWithAccess.map((employee) => ({
            ...employee,
            value: employee.id,
            label: employee.name,
          }));
        setEmployees(res.employeeListByFilterWithAccess);
        if (res.employeeListByFilterWithAccess.length === 1) {
          const taskHeader = {
            teacher: {
              ...res.employeeListByFilterWithAccess[0],
              value: res.employeeListByFilterWithAccess[0].id,
              label: res.employeeListByFilterWithAccess[0].name,
            },
          };

          change('taskHeader', taskHeader);
          loadSubjects(res.employeeListByFilterWithAccess[0].id);
        }
      })
      .catch(() => {
        showNotificationAutoDestroy(
          'danger',
          t('errors.title'),
          t('errors.error-to-loading')
        );
      });
  }

  const onEmployeeChangeHandler = (value) => {
    if (value) {
      loadSubjects(value.value);
    }
  };

  useEffect(() => {
    loadEmployees();
    loadRecipients();
  }, []);

  return (
    <Fragment>
      <FormSection name="taskHeader" className="form__section">
        <div className="form__form-group">
          <span className="form__form-group-label">
            {t('communication.teacher')}
          </span>
          <div className="form__form-group-field">
            <Field
              name="teacher"
              type="text"
              component={Select}
              isDisabled={!employees || !editable}
              options={employees}
              placeholder={t('communication.placeholders.teacher')}
              onChange={onEmployeeChangeHandler}
            />
          </div>
        </div>
        <div className="form__form-group">
          <span className="form__form-group-label">
            {t('communication.subject')}
          </span>
          <div className="form__form-group-field">
            <Field
              name="subject"
              type="text"
              component={Select}
              isDisabled={!subjects || !editable}
              options={subjects}
              placeholder={t('communication.placeholders.subject')}
            />
          </div>
        </div>
      </FormSection>
      <div className="form__form-group">
        <span className="form__form-group-label">
          {t(`communication.due-date-${COMMUNICATION_TYPE.ACTIVITY}`)}
        </span>
        <div className="form__form-group-field">
          <Field
            name="dueDate"
            minDate={new Date()}
            component={DateField}
            placeholder={t(
              `communication.placeholders.due-date-${COMMUNICATION_TYPE.ACTIVITY}`
            )}
            disabled={!editable}
          />
        </div>
      </div>
      <div className="form__form-group">
        <span className="form__form-group-label">
          {t('general.recipients')}
        </span>
        <div className="form_form-group-field">
          <Field
            name="recipients"
            isMulti
            closeMenuOnSelect={false}
            component={SelectVirtualized}
            options={recipients}
            disabled={!editable}
            onChange={onRecipientChange}
            placeholder={t('general.placeholders.select')}
          />
        </div>
      </div>
      <div className="form__form-group">
        <span className="form__form-group-label">{t('general.title')}</span>
        <div className="form__form-group-field">
          <Field
            name="title"
            type="text"
            placeholder={t('communication.placeholders.task-title')}
            component={renderField}
            maxLength="250"
            readOnly={!editable}
          />
        </div>
      </div>
    </Fragment>
  );
};

TaskFields.propTypes = {
  t: PropTypes.func.isRequired,
  showLoading: PropTypes.func.isRequired,
  hideLoading: PropTypes.func.isRequired,
  change: PropTypes.func.isRequired,
  editable: PropTypes.bool.isRequired,
};

export default withTranslation('common')(TaskFields);
